import { useState } from 'preact/hooks'

export function PrerequisitesWarning({ session, onDismiss }) {
  const [isDismissed, setIsDismissed] = useState(false)

  if (!session.prerequisites || isDismissed) return null

  const handleDismiss = () => {
    setIsDismissed(true)
    onDismiss?.(session.id)
  }

  const getWarningMessage = (prerequisites) => {
    const prereqLower = prerequisites.toLowerCase()
    
    if (prereqLower.includes('anthropic') || prereqLower.includes('claude')) {
      return {
        emoji: '🤖',
        title: "Wait! Don't go without your Anthropic key!",
        message: "You'll get screwed without it! Claude won't talk to you.",
        action: "You've got 5 minutes to set it up. Challenge accepted?",
        urgency: 'high'
      }
    }
    
    if (prereqLower.includes('openai') || prereqLower.includes('gpt')) {
      return {
        emoji: '🧠',
        title: "Hold up! Missing your OpenAI key?",
        message: "GPT will give you the silent treatment without it!",
        action: "Quick setup time - 3 minutes max. Ready?",
        urgency: 'medium'
      }
    }
    
    if (prereqLower.includes('dev dashboard') || prereqLower.includes('organization')) {
      return {
        emoji: '🏗️',
        title: "Oops! Need your Dev Dashboard setup!",
        message: "Can't build without the foundation!",
        action: "5-minute setup sprint. Let's go!",
        urgency: 'medium'
      }
    }
    
    if (prereqLower.includes('hydrogen') || prereqLower.includes('npm create')) {
      return {
        emoji: '⚡',
        title: "Hydrogen setup missing!",
        message: "Your storefront needs some juice!",
        action: "Quick npm install dance - 2 minutes!",
        urgency: 'low'
      }
    }
    
    if (prereqLower.includes('development store') || prereqLower.includes('store')) {
      return {
        emoji: '🏪',
        title: "Where's your dev store?",
        message: "Can't shop without a store!",
        action: "Store setup speedrun - 3 minutes!",
        urgency: 'medium'
      }
    }
    
    if (prereqLower.includes('rust-analyzer') || prereqLower.includes('rust') || prereqLower.includes('vs code extension')) {
      return {
        emoji: '🦀',
        title: "Rust tooling needed!",
        message: "Time to get rusty with the right tools!",
        action: "VS Code extension install - 30 seconds!",
        urgency: 'low'
      }
    }
    
    if (prereqLower.includes('liquiddoc') || prereqLower.includes('liquid') || prereqLower.includes('documentation')) {
      return {
        emoji: '📚',
        title: "Documentation homework time!",
        message: "Knowledge is power - don't wing it!",
        action: "Quick docs review - 10 minutes!",
        urgency: 'medium'
      }
    }
    
    if (prereqLower.includes('graphiql') || prereqLower.includes('graphql')) {
      return {
        emoji: '🔍',
        title: "GraphiQL setup required!",
        message: "Query like a pro, not a rookie!",
        action: "GraphiQL install - 2 minutes!",
        urgency: 'medium'
      }
    }
    
    if (prereqLower.includes('shopify cli') || prereqLower.includes('remix template') || prereqLower.includes('shopify flow')) {
      return {
        emoji: '⚙️',
        title: "Shopify CLI & tools needed!",
        message: "Professional developers use professional tools!",
        action: "CLI setup sprint - 5 minutes!",
        urgency: 'medium'
      }
    }
    
    if (prereqLower.includes('checkout') || prereqLower.includes('customer accounts') || prereqLower.includes('extensibility')) {
      return {
        emoji: '🛒',
        title: "Checkout extensibility setup!",
        message: "Modern checkout needs modern setup!",
        action: "Developer preview setup - 7 minutes!",
        urgency: 'medium'
      }
    }
    
    if (prereqLower.includes('markets') || prereqLower.includes('developer preview')) {
      return {
        emoji: '🌍',
        title: "Markets preview setup needed!",
        message: "Go global with the right foundation!",
        action: "Markets setup - 5 minutes!",
        urgency: 'medium'
      }
    }
    
    if (prereqLower.includes('plus plan') || prereqLower.includes('plus')) {
      return {
        emoji: '💎',
        title: "Plus plan required!",
        message: "Premium features need premium access!",
        action: "Plan upgrade check - 2 minutes!",
        urgency: 'high'
      }
    }
    
    // Generic fallback
    return {
      emoji: '⚠️',
      title: "Prerequisites needed!",
      message: "Don't get caught unprepared!",
      action: "Quick setup time - you got this!",
      urgency: 'low'
    }
  }

  const warning = getWarningMessage(session.prerequisites)
  
  const urgencyStyles = {
    high: 'from-red-500/20 to-orange-500/20 border-red-500/40',
    medium: 'from-yellow-500/20 to-orange-500/20 border-yellow-500/40',
    low: 'from-blue-500/20 to-purple-500/20 border-blue-500/40'
  }

  return (
    <div className={`bg-gradient-to-r ${urgencyStyles[warning.urgency]} border rounded-xl p-4 mb-4 animate-pulse`}>
      <div className="flex items-start gap-3">
        <div className="text-2xl flex-shrink-0">{warning.emoji}</div>
        <div className="flex-1 min-w-0">
          <h4 className="font-semibold text-white mb-1">{warning.title}</h4>
          <p className="text-sm text-gray-300 mb-2">{warning.message}</p>
          <p className="text-xs font-medium text-editions-orange mb-3">{warning.action}</p>
          
          {/* Prerequisites Details */}
          <details className="text-xs">
            <summary className="text-gray-400 cursor-pointer hover:text-white transition-colors">
              Show requirements
            </summary>
            <div className="mt-2 p-2 bg-black/20 rounded border border-white/10">
              <pre className="whitespace-pre-wrap text-gray-300 text-xs">
                {session.prerequisites}
              </pre>
            </div>
          </details>
        </div>
        
        <button
          onClick={handleDismiss}
          className="text-gray-400 hover:text-white transition-colors flex-shrink-0"
          title="Dismiss warning"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
    </div>
  )
} 